// Demo case seeder — populates a single realistic case ("Security
// Deposit Dispute — 742 Oak Street") so a fresh account can show the
// product end-to-end in a recording without setup. Stores the seeded
// case id in localStorage so "Clear demo case" can later remove it
// even if the user navigates away in between.

import { jsPDF } from "jspdf";
import { api } from "../api";
import type { Case, Direction, SourceType } from "../types";

const DEMO_CASE_LS_KEY = "casethread:demo-case-id";

export function getDemoCaseId(): string | null {
  try {
    return window.localStorage.getItem(DEMO_CASE_LS_KEY);
  } catch {
    return null;
  }
}

function rememberDemoCaseId(id: string): void {
  try {
    window.localStorage.setItem(DEMO_CASE_LS_KEY, id);
  } catch {
    /* localStorage blocked — clear-demo will fail silently if so */
  }
}

function forgetDemoCaseId(): void {
  try {
    window.localStorage.removeItem(DEMO_CASE_LS_KEY);
  } catch {
    /* ignore */
  }
}

// ---- placeholder file generators ------------------------------------------

// Render a "photo of a carpet" placeholder. We don't ship real images;
// instead we draw a beige rectangle with a noisy gray strip down the
// middle (the carpet) and overlay a label so the viewer can tell which
// is which in the timeline.
function makeCarpetPhoto(label: string, subtitle: string): Promise<File> {
  const width = 1200;
  const height = 800;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_unavailable");

  // Off-white room background
  ctx.fillStyle = "#f4efe6";
  ctx.fillRect(0, 0, width, height);

  // Carpet strip — slightly darker beige with random speckle for texture
  const carpetX = 120;
  const carpetY = 220;
  const carpetW = width - 240;
  const carpetH = 420;
  ctx.fillStyle = "#c9b89a";
  ctx.fillRect(carpetX, carpetY, carpetW, carpetH);

  // Add subtle speckle so it reads as textured fabric, not flat colour
  ctx.fillStyle = "rgba(120, 100, 70, 0.25)";
  for (let i = 0; i < 4000; i++) {
    const x = carpetX + Math.random() * carpetW;
    const y = carpetY + Math.random() * carpetH;
    ctx.fillRect(x, y, 2, 2);
  }

  // Skirting board hint
  ctx.fillStyle = "#e8ddc7";
  ctx.fillRect(0, carpetY - 30, width, 30);
  ctx.fillRect(0, carpetY + carpetH, width, 30);

  // Label band across the top
  ctx.fillStyle = "rgba(20, 20, 20, 0.78)";
  ctx.fillRect(0, 0, width, 140);

  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 44px -apple-system, Helvetica, Arial, sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText(label, 60, 60);

  ctx.font = "26px -apple-system, Helvetica, Arial, sans-serif";
  ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
  ctx.fillText(subtitle, 60, 105);

  // Footer caption inside the photo for redundancy
  ctx.fillStyle = "rgba(30, 30, 30, 0.65)";
  ctx.font = "italic 22px -apple-system, Helvetica, Arial, sans-serif";
  ctx.fillText("742 Oak Street, Unit 3B — living room", 60, height - 40);

  return new Promise<File>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("canvas_toblob_failed"));
          return;
        }
        const filename = label.toLowerCase().includes("move-in")
          ? "move-in-carpet.jpg"
          : "move-out-carpet.jpg";
        resolve(new File([blob], filename, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.9,
    );
  });
}

// Render a single-page PDF that looks like a real document. Uses jsPDF
// directly (already a dep, also used by the dossier exporter) so we
// don't pull in anything new for the demo.
function makePdf(filename: string, draw: (doc: jsPDF) => void): File {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  draw(doc);
  const blob = doc.output("blob");
  return new File([blob], filename, { type: "application/pdf" });
}

function makeDeductionNoticePdf(): File {
  return makePdf("deduction-notice.pdf", (doc) => {
    const margin = 56;
    let y = margin;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Torres Property Management", margin, y);
    y += 22;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(90, 90, 90);
    doc.text("1145 Pine Avenue · Suite 4 · Phone (555) 218-4490", margin, y);
    y += 30;

    doc.setTextColor(20, 20, 20);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("Security Deposit — Itemized Deduction Notice", margin, y);
    y += 24;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    const intro = [
      "Tenant: Sarah Chen",
      "Unit: 742 Oak Street, Unit 3B",
      "Move-out date: March 1, 2026",
      "Notice date: April 1, 2026",
    ];
    for (const line of intro) {
      doc.text(line, margin, y);
      y += 16;
    }
    y += 16;

    doc.setFont("helvetica", "bold");
    doc.text("Deductions", margin, y);
    y += 18;

    doc.setFont("helvetica", "normal");
    const rows: [string, string][] = [
      ["Carpet replacement — living room", "$1,650.00"],
      ["Carpet replacement — hallway", "$ 620.00"],
      ["Cleaning / haul-away", "$ 130.00"],
    ];
    for (const [label, amount] of rows) {
      doc.text(label, margin, y);
      doc.text(amount, 460, y, { align: "right" });
      y += 16;
    }

    y += 6;
    doc.setLineWidth(0.5);
    doc.line(margin, y, 470, y);
    y += 14;
    doc.setFont("helvetica", "bold");
    doc.text("Total deductions", margin, y);
    doc.text("$2,400.00", 460, y, { align: "right" });
    y += 22;

    doc.setFont("helvetica", "normal");
    doc.text("Security deposit on file", margin, y);
    doc.text("$2,400.00", 460, y, { align: "right" });
    y += 16;
    doc.setFont("helvetica", "bold");
    doc.text("Balance returned to tenant", margin, y);
    doc.text("$    0.00", 460, y, { align: "right" });
    y += 28;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(70, 70, 70);
    const note = doc.splitTextToSize(
      "Carpet replacement was necessary due to staining and wear in the living room and hallway exceeding normal use. Photographs of the damage are retained on file. Per the lease, tenant has 14 days to dispute these deductions in writing.",
      460,
    );
    for (const line of note) {
      doc.text(line, margin, y);
      y += 14;
    }

    y += 14;
    doc.setTextColor(20, 20, 20);
    doc.setFont("helvetica", "italic");
    doc.text("Mike Torres, Property Manager", margin, y);
  });
}

function makeLeaseAgreementPdf(): File {
  return makePdf("lease-agreement.pdf", (doc) => {
    const margin = 56;
    let y = margin;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Residential Lease Agreement", margin, y);
    y += 22;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(90, 90, 90);
    doc.text("742 Oak Street, Unit 3B · Effective April 1, 2024", margin, y);
    y += 28;

    doc.setTextColor(20, 20, 20);
    doc.setFontSize(11);

    const section = (heading: string, body: string) => {
      doc.setFont("helvetica", "bold");
      doc.text(heading, margin, y);
      y += 16;
      doc.setFont("helvetica", "normal");
      const wrapped = doc.splitTextToSize(body, 480);
      for (const line of wrapped) {
        doc.text(line, margin, y);
        y += 14;
      }
      y += 10;
    };

    section(
      "Section 1. Parties",
      "This agreement is entered into between Torres Property Management ('Landlord') and Sarah Chen ('Tenant') for the rental of the premises described above.",
    );

    section(
      "Section 2. Term",
      "The initial term is twenty-four (24) months commencing April 1, 2024 and ending March 31, 2026. Tenant has provided written notice of move-out effective March 1, 2026.",
    );

    section(
      "Section 7. Security Deposit",
      "Tenant has paid a security deposit of $2,400.00, held by Landlord against damages beyond normal wear and tear and unpaid amounts owed at the end of tenancy.",
    );

    // Highlight the deposit-return clause so the demo viewer can see
    // why this PDF matters at a glance.
    doc.setFillColor(255, 240, 170);
    doc.rect(margin - 6, y - 4, 504, 60, "F");
    doc.setFont("helvetica", "bold");
    doc.text("Section 8.2 — Deposit Return", margin, y + 10);
    doc.setFont("helvetica", "normal");
    const clause = doc.splitTextToSize(
      "Security deposit shall be returned within 30 days of move-out, less any deductions for damage beyond normal wear and tear.",
      480,
    );
    let cy = y + 28;
    for (const line of clause) {
      doc.text(line, margin, cy);
      cy += 14;
    }
    y = cy + 18;

    section(
      "Section 12. Notices",
      "All notices required under this agreement shall be in writing and delivered by email or first-class mail to the addresses on file.",
    );

    doc.setFont("helvetica", "italic");
    doc.setTextColor(70, 70, 70);
    doc.setFontSize(10);
    doc.text(
      "Signed: Sarah Chen · Mike Torres — April 1, 2024",
      margin,
      y + 10,
    );
  });
}

// ---- timeline definition --------------------------------------------------

interface DemoEntry {
  date: string; // YYYY-MM-DD
  direction: Direction;
  source_type: SourceType;
  from_addr: string | null;
  to_addr: string | null;
  subject: string;
  body_text: string;
  notes?: string;
  // When set, the seeder generates the file before posting and attaches
  // it via the file-upload + create-record pipeline. Without it, the
  // record is created with no attachment.
  file?: () => File | Promise<File>;
}

const TENANT_EMAIL = "sarah.chen@example.com";
const LANDLORD_EMAIL = "mike@torrespm.com";

function buildEntries(): DemoEntry[] {
  return [
    {
      date: "2026-03-01",
      direction: "inbound",
      source_type: "email",
      from_addr: `Mike Torres <${LANDLORD_EMAIL}>`,
      to_addr: `Sarah Chen <${TENANT_EMAIL}>`,
      subject: "Move-out confirmation",
      body_text:
        "Hi Sarah,\n\nConfirming your move-out from 742 Oak St unit 3B is complete as of today. The unit was left in good condition. We'll process your security deposit per the lease terms within 30 days.\n\nThanks,\nMike",
    },
    {
      date: "2026-03-15",
      direction: "outbound",
      source_type: "email",
      from_addr: `Sarah Chen <${TENANT_EMAIL}>`,
      to_addr: `Mike Torres <${LANDLORD_EMAIL}>`,
      subject: "Security deposit return timeline?",
      body_text:
        "Hi Mike,\n\nIt's been two weeks since move-out. Can you confirm when I should expect my $2,400 deposit back? Per our lease agreement, the return window is 30 days.\n\nThanks,\nSarah",
    },
    {
      date: "2026-04-01",
      direction: "inbound",
      source_type: "email",
      from_addr: `Mike Torres <${LANDLORD_EMAIL}>`,
      to_addr: `Sarah Chen <${TENANT_EMAIL}>`,
      subject: "Security deposit — damages",
      body_text:
        "Sarah,\n\nAfter inspection, we found carpet damage in the living room and hallway. The full $2,400 deposit will be applied toward carpet replacement. Attached is the itemized deduction.\n\nMike\n\n[Attachment: deduction-notice.pdf]",
      file: () => makeDeductionNoticePdf(),
    },
    {
      date: "2026-04-03",
      direction: "outbound",
      source_type: "email",
      from_addr: `Sarah Chen <${TENANT_EMAIL}>`,
      to_addr: `Mike Torres <${LANDLORD_EMAIL}>`,
      subject: "Disputing carpet damage charge",
      body_text:
        "Mike,\n\nI'm disputing this deduction. I have move-in and move-out photos showing the carpet was in the same condition. The wear shown is normal and was present when I moved in. Attaching both photos.\n\nI expect the full deposit returned within 14 days.\n\nSarah\n\n[Attachments: move-in-carpet.jpg, move-out-carpet.jpg — see separate timeline entries]",
    },
    {
      date: "2026-04-03",
      direction: "outbound",
      source_type: "other",
      from_addr: null,
      to_addr: null,
      subject: "Move-in photo — living room carpet",
      body_text:
        "Photo taken at move-in (April 2024) showing the living room carpet in good condition. Same area as the deduction claim.",
      file: () =>
        makeCarpetPhoto(
          "Move-in photo — Apr 2024",
          "carpet in good condition",
        ),
    },
    {
      date: "2026-04-03",
      direction: "outbound",
      source_type: "other",
      from_addr: null,
      to_addr: null,
      subject: "Move-out photo — living room carpet",
      body_text:
        "Photo taken at move-out (March 2026) showing the living room carpet in the same condition as move-in. No new staining or damage visible.",
      file: () =>
        makeCarpetPhoto(
          "Move-out photo — Mar 2026",
          "carpet in same condition",
        ),
    },
    {
      date: "2026-04-03",
      direction: "outbound",
      source_type: "letter",
      from_addr: null,
      to_addr: null,
      subject: "Lease agreement — deposit return clause",
      body_text:
        "Signed lease agreement for 742 Oak Street Unit 3B. Section 8.2 highlighted: 'Security deposit shall be returned within 30 days of move-out, less any deductions for damage beyond normal wear and tear.'",
      file: () => makeLeaseAgreementPdf(),
    },
    {
      date: "2026-04-05",
      direction: "outbound",
      source_type: "phone_call",
      from_addr: `Sarah Chen <${TENANT_EMAIL}>`,
      to_addr: "Torres Property Management — (555) 218-4490",
      subject: "Call with property manager",
      body_text:
        "Spoke with Diana at Torres Property Management. She said they'd \"review the photos and get back to me.\" I asked for a timeline — she said \"a few days.\" Reference #441. She did not dispute that the move-out confirmation said the unit was in good condition.",
      notes: "Reference #441",
    },
  ];
}

// ---- entry points ---------------------------------------------------------

export async function seedDemoCase(): Promise<Case> {
  // If a demo case already exists, surface it instead of creating a
  // duplicate. The caller can decide whether to navigate to it.
  const existingId = getDemoCaseId();
  if (existingId) {
    try {
      const { case: existing } = await api.getCase(existingId);
      return existing;
    } catch {
      // The stored id is stale (manually deleted) — fall through and
      // create a fresh demo case.
      forgetDemoCaseId();
    }
  }

  const { case: created } = await api.createCase(
    "Security Deposit Dispute — 742 Oak Street",
    "Demo case. $2,400 security deposit dispute with landlord Torres Property Management following move-out from 742 Oak Street, Unit 3B.",
  );
  rememberDemoCaseId(created.id);

  // Mark active. createCase defaults the status server-side; an
  // explicit PATCH guarantees the demo always shows up the same way.
  let withStatus = created;
  try {
    const { case: updated } = await api.updateCase(created.id, {
      status: "open",
    });
    withStatus = updated;
  } catch {
    /* non-fatal — the case still exists with the default status */
  }

  const entries = buildEntries();
  for (const entry of entries) {
    let fileMeta: {
      file_r2_key?: string | null;
      file_name?: string | null;
      file_mime_type?: string | null;
    } = {};
    if (entry.file) {
      const f = await entry.file();
      const upload = await api.uploadFile(created.id, f);
      fileMeta = {
        file_r2_key: upload.file_r2_key,
        file_name: upload.file_name,
        file_mime_type: upload.file_mime_type,
      };
    }

    await api.createRecord(created.id, {
      // Noon-UTC matches the convention used by the manual write-record
      // and date-repair paths so all records render on the intended
      // calendar day in any timezone.
      record_date: `${entry.date}T12:00:00.000Z`,
      direction: entry.direction,
      source_type: entry.source_type,
      from_addr: entry.from_addr,
      to_addr: entry.to_addr,
      subject: entry.subject,
      body_text: entry.body_text,
      notes: entry.notes ?? null,
      creation_source: "manual",
      date_source: "manual",
      ...fileMeta,
    });
  }

  return withStatus;
}

export async function clearDemoCase(): Promise<{ deleted: boolean }> {
  const id = getDemoCaseId();
  if (!id) return { deleted: false };
  try {
    await api.deleteCase(id);
  } catch (err) {
    // 404 means the case was already deleted manually; treat as success
    // and clean up the stored id. Anything else, surface to the caller.
    const msg = (err as Error).message;
    if (!/HTTP 404|not.?found/i.test(msg)) {
      throw err;
    }
  }
  forgetDemoCaseId();
  return { deleted: true };
}
